<script lang="ts">
    import type { Price, SaleTags } from '@shared/types/types';
    import DealHighlight from '@tile/components/deal-highlight/DealHighlight.svelte';
    import Installments from '@tile/components/installments/Installments.svelte';
    import PriceRow from '@tile/components/price-row/PriceRow.svelte';
    import PriceContainer from '@tile/components/price/Price.svelte';
    import RetailPrice from '@tile/components/retail-price/RetailPrice.svelte';
    import SaleTagsComponent from '@tile/components/sale-tags/SaleTags.svelte';
    import SuggestedRetailPriceExperiment from '@tile/components/suggested-retail-price-experiment/SuggestedRetailPriceExperiment.svelte';
    import type { UvpMode } from '@tile/components/suggested-retail-price-experiment/SuggestedRetailPriceExperiment.svelte';
    import SuggestedRetailPrice from '@tile/components/suggested-retail-price/SuggestedRetailPrice.svelte';
    import UnitPrice from '@tile/components/unit-price/UnitPrice.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import type { ExperimentGroups } from '@tilelist/domain/experiments/Experiments.ts';

    interface Props {
        price: Price;
        saleTags?: SaleTags;
        dealHighlight?: string;
        variationId: string;
        rule?: string;
        showInstallments?: boolean;
    }

    let { price, saleTags, dealHighlight, variationId, rule, showInstallments }: Props = $props();

    const onex = injectWiring().onex;

    const uvpMapping: Record<ExperimentGroups['UVP_DISPLAY'], UvpMode> = {
        E3823A: 'default',
        E3823B: 'defensive',
        E3823C: 'strike',
        E3823D: 'red',
        E3823E: 'strikeRed',
    };

    const uvpMode = $derived(onex.matchGroup('UVP_DISPLAY', uvpMapping));
    const effectiveSaleTags = $derived.by(() => {
        if (uvpMode === 'default') {
            return saleTags;
        }

        return saleTags ? { ...saleTags, discount: undefined } : saleTags;
    });
</script>

{#snippet priceContent()}
    <DealHighlight {dealHighlight} />
    <UnitPrice unitPrice={price.normPrice} />
    {#if showInstallments}
        <Installments installmentPlan={price.installmentPlan} />
    {/if}
    <SaleTagsComponent saleTags={effectiveSaleTags} {variationId} {rule} />
{/snippet}

<PriceContainer>
    {#if uvpMode === 'default'}
        <!-- E3823A: Control — UVP shown only on sale -->
        <PriceRow>
            <RetailPrice {price} />
            <SuggestedRetailPrice {price} />
        </PriceRow>
    {:else if uvpMode === 'defensive'}
        <!-- E3823B: UVP always shown, no strikethrough, retail price black -->
        <PriceRow>
            <RetailPrice {price} uvpMode="defensive" />
            <SuggestedRetailPriceExperiment {price} uvpMode="defensive" />
        </PriceRow>
    {:else if uvpMode === 'strike'}
        <!-- E3823C: UVP always shown, with strikethrough, retail price black -->
        <PriceRow>
            <RetailPrice {price} uvpMode="strike" />
            <SuggestedRetailPriceExperiment {price} uvpMode="strike" />
        </PriceRow>
    {:else if uvpMode === 'red'}
        <!-- E3823D: UVP always shown, no strikethrough, retail price red -->
        <PriceRow>
            <RetailPrice {price} uvpMode="red" />
            <SuggestedRetailPriceExperiment {price} uvpMode="red" />
        </PriceRow>
    {:else if uvpMode === 'strikeRed'}
        <!-- E3823E: UVP always shown, with strikethrough, retail price red -->
        <PriceRow>
            <RetailPrice {price} uvpMode="strikeRed" />
            <SuggestedRetailPriceExperiment {price} uvpMode="strikeRed" />
        </PriceRow>
    {/if}
    {@render priceContent()}
</PriceContainer>
